/**
 * NeighborsList Component
 * 
 * Displays a list of neighboring properties grouped by tier.
 * Supports filtering, sorting, and analytics views.
 */

import React, { useState, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { NeighborsByTier, GeoFeatureSummary } from '../../shared/types/parcel';
import { getTierColor } from './ParcelOverlay';

// ============================================
// Types
// ============================================

interface NeighborsListProps {
  /** Neighbors grouped by tier */
  neighborsByTier: NeighborsByTier[];
  /** Loading state */
  loading?: boolean;
  /** Callback when a neighbor is selected */
  onNeighborSelect?: (neighbor: GeoFeatureSummary, tier: number) => void;
  /** Currently selected neighbor ID */
  selectedNeighborId?: number | null;
  /** Show analytics summary */
  showAnalytics?: boolean;
  /** Maximum height for the list */
  maxHeight?: number;
  /** Compact mode for sidebars */
  compact?: boolean;
}

type SortOption = 'name' | 'acres' | 'value';
type FilterTier = 'all' | 1 | 2 | 3;

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value?: number | string | null): string {
  if (value == null) return 'N/A';
  const numValue = Number(value);
  if (isNaN(numValue)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numValue);
}

function formatAcreage(acres?: number | string | null): string {
  if (acres == null) return 'N/A';
  const numAcres = Number(acres);
  if (isNaN(numAcres)) return 'N/A';
  return `${numAcres.toFixed(2)} ac`;
}

// ============================================
// Analytics Summary Component
// ============================================

interface AnalyticsSummaryProps {
  neighborsByTier: NeighborsByTier[];
}

function AnalyticsSummary({ neighborsByTier }: AnalyticsSummaryProps) {
  const styles = useStyles();

  // Calculate totals
  const totals = useMemo(() => {
    let totalParcels = 0;
    let totalAcres = 0;
    let totalValue = 0;
    const owners = new Set<string>();

    for (const tier of neighborsByTier) {
      totalParcels += tier.total_count || 0;
      totalAcres += Number(tier.total_acres) || 0;
      for (const neighbor of tier.neighbors) {
        if (neighbor.total_value) totalValue += Number(neighbor.total_value) || 0;
        if (neighbor.owner_name) owners.add(neighbor.owner_name);
      }
    }

    return {
      totalParcels,
      totalAcres,
      totalValue,
      uniqueOwners: owners.size,
    };
  }, [neighborsByTier]);

  // Find largest landowner
  const largestOwner = useMemo(() => {
    const ownerAcres: Record<string, number> = {};
    
    for (const tier of neighborsByTier) {
      for (const neighbor of tier.neighbors) {
        if (neighbor.owner_name && neighbor.area_acres) {
          const acres = Number(neighbor.area_acres) || 0;
          ownerAcres[neighbor.owner_name] = (ownerAcres[neighbor.owner_name] || 0) + acres;
        }
      }
    }

    let maxOwner = '';
    let maxAcres = 0;
    for (const [owner, acres] of Object.entries(ownerAcres)) {
      if (acres > maxAcres) {
        maxOwner = owner;
        maxAcres = acres;
      }
    }

    return maxOwner ? { name: maxOwner, acres: maxAcres } : null;
  }, [neighborsByTier]);

  return (
    <View
      style={{
        backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.08),
        borderRadius: styles.utils.radius.md,
        padding: styles.utils.spacing.md,
        marginBottom: styles.utils.spacing.md,
      }}
    >
      <Text
        style={[
          styles.text('sm'),
          { fontWeight: styles.utils.typography.weights.semibold, marginBottom: styles.utils.spacing.sm },
        ]}
      >
        Neighborhood Analytics
      </Text>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: styles.utils.spacing.md }}>
        <View style={{ minWidth: 80 }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            Parcels
          </Text>
          <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold }]}>
            {totals.totalParcels}
          </Text>
        </View>

        <View style={{ minWidth: 80 }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            Total Acres
          </Text>
          <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold }]}>
            {(totals.totalAcres || 0).toFixed(1)}
          </Text>
        </View>

        <View style={{ minWidth: 80 }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            Owners
          </Text>
          <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold }]}>
            {totals.uniqueOwners}
          </Text>
        </View>

        <View style={{ minWidth: 100 }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            Total Value
          </Text>
          <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold }]}>
            {formatCurrency(totals.totalValue)}
          </Text>
        </View>
      </View>

      {largestOwner && (
        <View style={{ marginTop: styles.utils.spacing.sm, paddingTop: styles.utils.spacing.sm, borderTopWidth: 1, borderTopColor: styles.tokens.color.border }}>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            Largest Neighboring Landowner
          </Text>
          <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]}>
            {largestOwner.name} ({formatAcreage(largestOwner.acres)})
          </Text>
        </View>
      )}
    </View>
  );
}

// ============================================
// Neighbor Item Component
// ============================================

interface NeighborItemProps {
  neighbor: GeoFeatureSummary;
  tier: number;
  isSelected?: boolean;
  onSelect?: () => void;
  compact?: boolean;
}

function NeighborItem({ neighbor, tier, isSelected, onSelect, compact }: NeighborItemProps) {
  const styles = useStyles();
  const color = getTierColor(tier);

  return (
    <Pressable
      onPress={onSelect}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        padding: compact ? styles.utils.spacing.sm : styles.utils.spacing.md,
        backgroundColor: isSelected 
          ? addColorOpacity(color.fill, 0.2) 
          : pressed 
            ? addColorOpacity(styles.tokens.color.primary, 0.05)
            : 'transparent',
        borderBottomWidth: 1,
        borderBottomColor: styles.tokens.color.border,
        gap: styles.utils.spacing.sm,
      })}
    >
      {/* Tier indicator */}
      <View
        style={{
          width: compact ? 8 : 12,
          height: compact ? 8 : 12,
          backgroundColor: color.fill,
          borderRadius: 2,
        }}
      />

      {/* Info */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={[
            styles.text(compact ? 'xs' : 'sm'),
            { fontWeight: styles.utils.typography.weights.medium },
          ]}
          numberOfLines={1}
        >
          {neighbor.owner_name || 'Unknown Owner'}
        </Text>
        {neighbor.site_address && (
          <Text
            style={[
              styles.text('xs'),
              { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) },
            ]}
            numberOfLines={1}
          >
            {neighbor.site_address}
          </Text>
        )}
      </View>

      {/* Stats */}
      {!compact && (
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]}>
            {formatAcreage(neighbor.area_acres)}
          </Text>
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
            {formatCurrency(neighbor.total_value)}
          </Text>
        </View>
      )}

      {/* Arrow */}
      <Ionicons
        name="chevron-forward"
        size={16}
        color={addColorOpacity(styles.tokens.color.onSurface, 0.4)}
      />
    </Pressable>
  );
}

// ============================================
// Main NeighborsList Component
// ============================================

export function NeighborsList({
  neighborsByTier,
  loading = false,
  onNeighborSelect,
  selectedNeighborId,
  showAnalytics = true,
  maxHeight,
  compact = false,
}: NeighborsListProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [filterTier, setFilterTier] = useState<FilterTier>('all');

  // Filter and sort neighbors
  const filteredNeighbors = useMemo(() => {
    const result: Array<{ neighbor: GeoFeatureSummary; tier: number }> = [];

    for (const tierData of neighborsByTier) {
      if (filterTier !== 'all' && tierData.tier !== filterTier) continue;

      for (const neighbor of tierData.neighbors) {
        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesName = neighbor.owner_name?.toLowerCase().includes(query);
          const matchesAddress = neighbor.site_address?.toLowerCase().includes(query);
          if (!matchesName && !matchesAddress) continue;
        }

        result.push({ neighbor, tier: tierData.tier });
      }
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.neighbor.owner_name || '').localeCompare(b.neighbor.owner_name || '');
        case 'acres':
          return (b.neighbor.area_acres || 0) - (a.neighbor.area_acres || 0);
        case 'value':
          return (b.neighbor.total_value || 0) - (a.neighbor.total_value || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [neighborsByTier, searchQuery, sortBy, filterTier]);

  if (loading) {
    return (
      <View style={{ padding: styles.utils.spacing.xl, alignItems: 'center' }}>
        <ActivityIndicator size="large" color={styles.tokens.color.primary} />
        <Text style={[styles.text('sm'), { marginTop: styles.utils.spacing.md }]}>
          Loading neighbors...
        </Text>
      </View>
    );
  }

  if (neighborsByTier.length === 0) {
    return (
      <View
        style={{
          padding: styles.utils.spacing.xl,
          alignItems: 'center',
          backgroundColor: addColorOpacity(styles.tokens.color.surface, 0.5),
          borderRadius: styles.utils.radius.md,
        }}
      >
        <Ionicons name="people-outline" size={48} color={addColorOpacity(styles.tokens.color.onSurface, 0.3)} />
        <Text style={[styles.text('base'), { marginTop: styles.utils.spacing.md, textAlign: 'center' }]}>
          No neighbor data available
        </Text>
        <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6), textAlign: 'center', marginTop: styles.utils.spacing.xs }]}>
          Link this property to parcel data to see neighboring properties.
        </Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {/* Analytics Summary */}
      {showAnalytics && !compact && (
        <AnalyticsSummary neighborsByTier={neighborsByTier} />
      )}

      {/* Search and Filters */}
      <View style={{ marginBottom: styles.utils.spacing.md }}>
        {/* Search */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: styles.tokens.color.surface,
            borderRadius: styles.utils.radius.md,
            paddingHorizontal: styles.utils.spacing.sm,
            marginBottom: styles.utils.spacing.sm,
          }}
        >
          <Ionicons name="search" size={18} color={addColorOpacity(styles.tokens.color.onSurface, 0.5)} />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search by owner or address..."
            placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.4)}
            style={[
              styles.text('sm'),
              {
                flex: 1,
                paddingVertical: styles.utils.spacing.sm,
                paddingHorizontal: styles.utils.spacing.sm,
                color: styles.tokens.color.onSurface,
              },
            ]}
          />
          {searchQuery.length > 0 && (
            <Pressable onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={18} color={addColorOpacity(styles.tokens.color.onSurface, 0.5)} />
            </Pressable>
          )}
        </View>

        {/* Filter/Sort Row */}
        <View style={{ flexDirection: 'row', gap: styles.utils.spacing.sm }}>
          {/* Tier Filter */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', gap: styles.utils.spacing.xs }}>
              {(['all', 1, 2, 3] as FilterTier[]).map((tier) => (
                <Pressable
                  key={tier}
                  onPress={() => setFilterTier(tier)}
                  style={{
                    paddingHorizontal: styles.utils.spacing.sm,
                    paddingVertical: styles.utils.spacing.xs,
                    borderRadius: styles.utils.radius.full,
                    backgroundColor: filterTier === tier 
                      ? styles.tokens.color.primary 
                      : styles.tokens.color.surface,
                  }}
                >
                  <Text
                    style={[
                      styles.text('xs'),
                      {
                        color: filterTier === tier 
                          ? styles.tokens.color.onPrimary 
                          : styles.tokens.color.onSurface,
                      },
                    ]}
                  >
                    {tier === 'all' ? 'All' : `Tier ${tier}`}
                  </Text>
                </Pressable>
              ))}
            </View>
          </ScrollView>

          {/* Sort Dropdown */}
          <Pressable
            onPress={() => {
              // Cycle through sort options
              const options: SortOption[] = ['name', 'acres', 'value'];
              const currentIndex = options.indexOf(sortBy);
              setSortBy(options[(currentIndex + 1) % options.length]);
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: styles.utils.spacing.sm,
              paddingVertical: styles.utils.spacing.xs,
              backgroundColor: styles.tokens.color.surface,
              borderRadius: styles.utils.radius.md,
              gap: styles.utils.spacing.xs,
            }}
          >
            <Ionicons name="swap-vertical" size={14} color={styles.tokens.color.onSurface} />
            <Text style={[styles.text('xs')]}>
              {sortBy === 'name' ? 'Name' : sortBy === 'acres' ? 'Acres' : 'Value'}
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Results count */}
      <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6), marginBottom: styles.utils.spacing.sm }]}>
        {filteredNeighbors.length} neighbor{filteredNeighbors.length !== 1 ? 's' : ''}
      </Text>

      {/* Neighbors List */}
      <ScrollView
        style={{ maxHeight: maxHeight || (isMobile ? 400 : 500) }}
        showsVerticalScrollIndicator
      >
        {filteredNeighbors.map(({ neighbor, tier }) => (
          <NeighborItem
            key={neighbor.id}
            neighbor={neighbor}
            tier={tier}
            isSelected={selectedNeighborId === neighbor.id}
            onSelect={() => onNeighborSelect?.(neighbor, tier)}
            compact={compact}
          />
        ))}

        {filteredNeighbors.length === 0 && (
          <View style={{ padding: styles.utils.spacing.lg, alignItems: 'center' }}>
            <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.6) }]}>
              No neighbors match your search
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

export default NeighborsList;

